/**
 * Mokkoning product-page embed.
 *
 * Reads the per-product configuration from `window.mokkoningData` (injected
 * by the plugin), mounts the 3D previewer in place of the product gallery,
 * binds the store's existing color/text fields, and intercepts add-to-cart to
 * generate + upload the print-ready file.
 */

import { Previews, ModeRegistry } from '@printapp/previews-core';
import { createThreeMode, type ThreeViewMode } from '@printapp/previews-3d';
import type { MokkoningConfig, MokkoningData } from './config';
import { bindColorSelect, bindTextInput } from './bindings';
import { buildTextOptions, generatePrintBlob } from './print';
import { uploadPrintFile } from './upload';
import { showError, clearNotice } from './notices';

const HOST_ID = 'mokkoning-3d-host';

void main().catch((error) => {
    console.error('[mokkoning] embed failed to start:', error);
});

async function main(): Promise<void> {
    const data = window.mokkoningData;
    if (!data?.config?.enabled) {
        return;
    }
    const cfg = data.config;

    await domReady();

    if (cfg.text.fontUrl) {
        injectFontStylesheet(cfg.text.fontUrl);
    }

    const host = mountHost();
    if (!host) {
        console.warn('[mokkoning] no mount point found — leaving the page untouched');
        return;
    }

    let previews: Previews;
    try {
        previews = await createPreviewer(host, cfg);
    } catch (error) {
        console.error('[mokkoning] previewer failed to mount:', error);
        host.remove();
        return;
    }

    const mode = (): ThreeViewMode | undefined => previews.getMode<ThreeViewMode>();

    const slots = mode()?.getSlots() ?? [];
    if (slots.length === 0) {
        console.warn('[mokkoning] no slots resolved — model part names likely differ from the configured slot names');
    }

    // Aspect ratio drives the text-canvas height: prefer the model's physical
    // artwork-slot metrics, fall back to the template image's own ratio.
    const templateAspect = cfg.template.url ? await imageAspectRatio(cfg.template.url).catch(() => 1) : 1;
    const aspectRatio = (): number => {
        try {
            return mode()?.getSlotMetrics(cfg.artSlot)?.aspectRatio ?? templateAspect;
        } catch {
            return templateAspect;
        }
    };

    const applyText = async (): Promise<void> => {
        if (!cfg.text.enabled && !cfg.template.url) return;
        try {
            await mode()?.setText(cfg.artSlot, buildTextOptions(cfg, getText(), cfg.text.canvasWidth, aspectRatio()));
        } catch (error) {
            console.error('[mokkoning] setText:', error);
        }
    };

    const getColor = bindColorSelect(cfg, mode);
    const getText = bindTextInput(cfg, () => void applyText());

    void applyText();

    interceptAddToCart(data, cfg, { getText, getColor, aspectRatio });
}

// ── DOM helpers ──────────────────────────────────────────────────────────────

function domReady(): Promise<void> {
    return new Promise((resolve) => {
        if (document.readyState !== 'loading') resolve();
        else document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
    });
}

function injectFontStylesheet(url: string): void {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
}

/**
 * Desktop: replace the product gallery contents with the viewer host.
 * Mobile: insert the host just above the option selectors so both are visible.
 */
function mountHost(): HTMLElement | null {
    const gallery = document.querySelector('.woocommerce-product-gallery');
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    const host = document.createElement('div');
    host.id = HOST_ID;
    host.style.cssText = 'width:100%;height:480px;';

    if (isMobile) {
        const target =
            document.querySelector('.variations') ??
            document.querySelector('form.cart');
        if (target?.parentNode) {
            host.style.marginBottom = '24px';
            target.parentNode.insertBefore(host, target);
            return host;
        }
    }
    if (gallery) {
        gallery.replaceChildren(host);
        return host;
    }
    return null;
}

// ── Previewer ────────────────────────────────────────────────────────────────

async function createPreviewer(host: HTMLElement, cfg: MokkoningConfig): Promise<Previews> {
    const registry = new ModeRegistry();
    registry.register('3d', createThreeMode);

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const zoom = isMobile ? cfg.camera.mobileZoom : 1;
    const position = cfg.camera.position.map((v) => v * zoom) as [number, number, number];

    return Previews.create(
        host,
        {
            mode: '3d',
            model: {
                url: cfg.model.url,
                format: cfg.model.format,
                upAxis: cfg.model.upAxis,
                noMtl: cfg.model.noMtl,
            },
            camera: {
                position,
                target: cfg.camera.target,
                fov: cfg.camera.fov,
                minDistance: 1,
                maxDistance: 20,
                autoRotate: false,
            },
            lights: [
                { type: 'ambient', color: '#ebf6ff', intensity: 0.5 },
                { type: 'directional', color: '#ffffff', intensity: 1.6, position: [-6, 8, 6], followCamera: true },
                { type: 'point', color: '#ffffff', intensity: 120, position: [12, 8, 6] },
            ],
            slots: [
                ...cfg.colorBinding.slots.map((name) => ({
                    name,
                    role: 'color' as const,
                    defaultColor: '#ffffff',
                    roughness: 0.1,
                })),
                { name: 'rest', role: 'color' as const, defaultColor: '#ffffff', roughness: 0.1 },
                {
                    name: cfg.artSlot,
                    role: 'artwork' as const,
                    defaultColor: '#ffffff',
                    roughness: 0.1,
                    // Matches the live mokkoning.nl integration for the bundled mug.
                    artwork: { flipU: false, flipV: false },
                },
            ],
            background: { color: '#FFFFFF' },
            ui: { zoom: true, fullscreen: true, reset: true },
        },
        { registry },
    );
}

function imageAspectRatio(src: string): Promise<number> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve((image.naturalWidth || 1) / (image.naturalHeight || 1));
        image.onerror = reject;
        image.src = src;
    });
}

// ── Add-to-cart interception ─────────────────────────────────────────────────

interface DesignSources {
    getText: () => string;
    getColor: () => { value: string; hex: string };
    aspectRatio: () => number;
}

function interceptAddToCart(data: MokkoningData, cfg: MokkoningConfig, sources: DesignSources): void {
    const form = document.querySelector<HTMLFormElement>('form.cart');
    if (!form) {
        console.warn('[mokkoning] form.cart not found — print file generation disabled');
        return;
    }

    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'mokkoning_token';
    form.appendChild(tokenInput);

    let bypass = false;

    form.addEventListener('submit', (event) => {
        if (bypass) {
            bypass = false;
            return;
        }
        event.preventDefault();
        void handleSubmit();
    });

    async function handleSubmit(): Promise<void> {
        clearNotice();
        const submitButton = form!.querySelector<HTMLButtonElement>('[type="submit"]');
        const originalLabel = submitButton?.textContent ?? '';
        if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = data.i18n.generating;
        }

        const restoreButton = (): void => {
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = originalLabel;
            }
        };

        const resubmit = (): void => {
            bypass = true;
            restoreButton();
            form!.requestSubmit();
        };

        try {
            const text = sources.getText();
            const color = sources.getColor();
            const aspect = sources.aspectRatio();

            const blob = await generatePrintBlob(cfg, text, aspect);
            const design = {
                text,
                colorValue: color.value,
                colorHex: color.hex,
                colorSlots: cfg.colorBinding.slots,
                font: cfg.text.font,
                fontSize: cfg.text.fontSize,
                textColor: cfg.text.color,
                x: cfg.text.x,
                y: cfg.text.y,
                canvasWidth: cfg.text.canvasWidth,
                printWidth: cfg.print.width,
                printHeight: Math.round(cfg.print.width / aspect),
                templateUrl: cfg.template.url,
                modelUrl: cfg.model.url,
                artSlot: cfg.artSlot,
            };

            const result = await uploadPrintFile(data, blob, design);
            tokenInput.value = result.token;
            resubmit();
        } catch (error) {
            console.error('[mokkoning] print file generation/upload failed:', error);
            if (cfg.onError === 'allow') {
                const errorInput = document.createElement('input');
                errorInput.type = 'hidden';
                errorInput.name = 'mokkoning_error';
                errorInput.value = '1';
                form!.appendChild(errorInput);
                resubmit();
            } else {
                restoreButton();
                showError(form!, data.i18n.uploadFailed);
            }
        }
    }
}
